import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import {
    BUTTON_ICON_SIZE_MEDIUM_DESKTOP,
    BUTTON_ICON_SIZE_MEDIUM_MOBILE,
} from '@pkg/components/Sections/Profile/Buttons';
import { styledLayout, styledReset } from '@pkg/primitives';

import { tokens } from '../../tokens';

import {
    mainChildTransition,
    MainChildVariant,
    mainChildVariants,
    PrefixActionVariant,
    prefixActionVariants,
    searchBackgroundCircleVariants,
    searchBackgroundTransition,
    SearchBackgroundVariant,
    searchBackgroundVariants,
    suffixActionTransition,
    SuffixActionVariant,
    suffixActionVariants,
} from './Header.motion';

interface MainProps {
    $largeTitle?: boolean;
    $searchMode?: boolean;
}

interface TitleProps {
    $largeTitle?: boolean;
}

interface SuffixProps {
    $disabled?: boolean;
}

export const HeaderWrapper = styled(motion.div)`
    flex: 0 0 ${tokens.header.height.mobile}px;
    position: relative;
    overflow: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        flex-basis: ${tokens.header.height.desktop}px;
    }
`;

export const SearchBackground = styled(motion.div).attrs({
    variants: searchBackgroundVariants,
    initial: SearchBackgroundVariant.Hidden,
    animate: SearchBackgroundVariant.Show,
    exit: SearchBackgroundVariant.Hidden,
    transition: searchBackgroundTransition,
})`
    ${styledLayout.positionCover()}
    z-index: 1;
    pointer-events: none;
    overflow: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        // Required to duplicate the <Sheet.Card /> top corners,
        // as Safari will not correctly "cut-off" the overflow.
        // Otherwise, this implementation could be much simpler.
        border-top-left-radius: ${tokens.card.radius}px;
        border-top-right-radius: ${tokens.card.radius}px;
    }
`;

export const SearchBackgroundCircle = styled(motion.div).attrs({
    variants: searchBackgroundCircleVariants,
    initial: SearchBackgroundVariant.Hidden,
    animate: SearchBackgroundVariant.Show,
    exit: SearchBackgroundVariant.Hidden,
    transition: searchBackgroundTransition,
})`
    position: absolute;
    top: 0;
    left: 0;
    width: ${tokens.header.height.mobile}px;
    height: ${tokens.header.height.mobile}px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.shades.white['20']};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: ${tokens.header.height.desktop}px;
        height: ${tokens.header.height.desktop}px;
    }
`;

export const Interior = styled.div`
    position: relative;
    z-index: 2;
    display: grid;
    // NOTE: Both align props are needed to solve a strange
    // height + alignment bug with the Search input.
    align-content: center;
    align-items: center;
    gap: ${tokens.spacing.tight}px;
    padding: ${tokens.spacing.tight}px;
    width: 100%;
    height: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding-left: ${tokens.spacing.loose}px;
    }
`;

export const Prefix = styled.div`
    grid-area: 1 / 1;
    justify-self: start;
`;

export const PrefixAction = styled(motion.div).attrs({
    variants: prefixActionVariants,
    initial: PrefixActionVariant.Hidden,
    animate: PrefixActionVariant.Show,
    exit: PrefixActionVariant.Hidden,
})``;

export const Main = styled.div<MainProps>`
    --yat_sheet-header-main-padding: ${BUTTON_ICON_SIZE_MEDIUM_MOBILE + tokens.spacing.tight}px;
    // TODO: Long tab lengths will break this layout.
    // We can make the container scroll horizontally...
    // but that probably isn't the best UI. Consider
    // converting "tabs" to a "dropdown select".
    display: grid;
    grid-area: 1/1;
    padding-left: var(--yat_sheet-header-main-padding);

    ${({ $largeTitle }) =>
        $largeTitle &&
        css`
            padding-left: 0;
            justify-self: start;
        `};

    ${({ $searchMode }) =>
        $searchMode
            ? css`
                  padding-right: 0;

                  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
                      padding-right: var(--yat_sheet-header-main-padding);
                  }
              `
            : css`
                  padding-right: var(--yat_sheet-header-main-padding);
              `}

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        --yat_sheet-header-main-padding: ${BUTTON_ICON_SIZE_MEDIUM_DESKTOP + tokens.spacing.tight}px;
    }
`;

export const MainChild = styled(motion.div).attrs({
    variants: mainChildVariants,
    initial: MainChildVariant.Hidden,
    animate: MainChildVariant.Show,
    exit: MainChildVariant.Hidden,
    transition: mainChildTransition,
})`
    grid-area: 1 / 1;
`;

export const Title = styled.h3<TitleProps>`
    ${styledReset.typography};
    font-family: 'Alliance No.1';
    font-weight: 700;
    font-size: 18px;
    line-height: 1;
    letter-spacing: -0.02em;
    text-align: center;
    color: white;

    ${({ $largeTitle }) =>
        $largeTitle &&
        css`
            font-size: 32px;
            text-align: left;
        `};
`;

export const Suffix = styled.div<SuffixProps>`
    grid-area: 1 / 1;
    justify-self: end;
    transition-property: opacity, transform;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-delay: ${({ theme }) => theme.transitions.speeds.rapid}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    // NOTE: Using this strategy instead of 'framer-motion' to "fade-out"
    // because we want to preserve the real-estate and not cause
    // a sudden layout shift.
    ${({ $disabled }) =>
        $disabled &&
        css`
            pointer-events: none;
            opacity: 0;
            transform: translateX(25%);
        `}
`;

export const SuffixAction = styled(motion.div).attrs({
    variants: suffixActionVariants,
    initial: SuffixActionVariant.Hidden,
    animate: SuffixActionVariant.Show,
    exit: SuffixActionVariant.Hidden,
    transition: suffixActionTransition,
})``;
